import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { EventCard, StatusBadge } from "@/components/feature/EventCard";
import { getEventById, getRelatedEvents } from "@/utils/events";
import { setPageSeo } from "@/utils/seo";

const brandSuffix = "51每日大赛今日黑聚集地详情";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const event = id ? getEventById(id) : undefined;

  useEffect(() => {
    if (event) {
      setPageSeo({
        title: `${event.title}详情 - ${brandSuffix}`,
        description: `${event.title}将于${event.date}在${event.venue}举行，比赛项目为${event.format}。${event.summary}`,
        keywords: `${event.title},${event.category},${event.region},赛事详情,比赛资讯`,
      });
    } else {
      setPageSeo({
        title: `赛事信息暂未公布 - ${brandSuffix}`,
        description: "该赛事信息暂未公布，请返回赛事分类页浏览其他赛事。",
      });
    }
  }, [event]);

  if (!event) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-4xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-extrabold text-foreground-950">
          赛事信息暂未公布
        </h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          你访问的赛事可能已被移除或尚未开放，请返回赛事分类页浏览其他赛事内容。
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/category"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回赛事分类
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400"
          >
            回到首页
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedEvents(event, 3);

  return (
    <div>
      <section className="relative w-full">
        <div className="relative h-[320px] w-full overflow-hidden md:h-[420px]">
          <img
            src={event.cover}
            alt={`${event.title} 赛事封面`}
            title={`${event.title} 赛事资讯`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/60 to-foreground-950/30"></div>
        </div>

        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-[1280px] px-4 pb-8 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-background-300 md:text-sm">
              <Link to="/" className="transition-colors hover:text-accent-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/category" className="transition-colors hover:text-accent-400">
                赛事分类
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-100">{event.category}</span>
            </nav>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
                {event.category}
              </span>
              <StatusBadge status={event.status} />
            </div>
            <h1 className="mt-3 max-w-3xl font-heading text-2xl font-extrabold leading-tight text-background-50 md:text-4xl">
              {event.title}
            </h1>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
          <div>
            <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                赛事简介
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                {event.summary}
              </p>
              <h3 className="mt-8 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-article-line text-primary-600"></i>
                详细赛况
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                {event.description}
              </p>
            </section>

            <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                赛程安排
              </h2>
              <ol className="mt-6 space-y-4">
                {event.schedule.map((item) => (
                  <li key={item.time} className="relative flex gap-4 pl-6">
                    <span className="absolute left-0 top-1.5 flex h-3 w-3 items-center justify-center">
                      <span className="h-3 w-3 rounded-full border-2 border-accent-500 bg-background-50"></span>
                    </span>
                    <span className="absolute left-[5px] top-5 h-[calc(100%-4px)] w-px bg-background-300"></span>
                    <div className="flex flex-col gap-1 pb-2 sm:flex-row sm:items-start sm:gap-4">
                      <span className="w-28 shrink-0 font-heading text-sm font-bold text-primary-700">
                        {item.time}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-foreground-950">{item.title}</p>
                        <p className="mt-0.5 text-sm text-foreground-600">{item.detail}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                实时数据
              </h2>
              <p className="mt-2 text-xs text-foreground-500">
                以下数据为演示内容，用于展示赛事数据模块的呈现效果。
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {event.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg border border-background-200 bg-background-100 p-5 text-center"
                  >
                    <p className="font-heading text-2xl font-extrabold text-accent-600">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">基本信息</h3>
              <ul className="mt-4 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <i className="ri-calendar-line mt-0.5 text-lg text-primary-600"></i>
                  <div>
                    <p className="text-xs text-foreground-500">比赛时间</p>
                    <p className="mt-0.5 font-medium text-foreground-900">{event.date}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line mt-0.5 text-lg text-primary-600"></i>
                  <div>
                    <p className="text-xs text-foreground-500">举办地点</p>
                    <p className="mt-0.5 font-medium text-foreground-900">{event.venue}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">地区：{event.region}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-gamepad-line mt-0.5 text-lg text-primary-600"></i>
                  <div>
                    <p className="text-xs text-foreground-500">比赛项目</p>
                    <p className="mt-0.5 font-medium text-foreground-900">{event.format}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-team-line mt-0.5 text-lg text-primary-600"></i>
                  <div>
                    <p className="text-xs text-foreground-500">参赛队伍 / 选手</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {event.teams.map((team) => (
                        <span
                          key={team}
                          className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800"
                        >
                          {team}
                        </span>
                      ))}
                    </div>
                  </div>
                </li>
              </ul>

              <div className="mt-5 flex items-center justify-between rounded-md bg-background-100 px-4 py-3">
                <span className="text-xs text-foreground-500">赛事热度</span>
                <span className="flex items-center gap-1 font-heading text-lg font-extrabold text-accent-600">
                  <i className="ri-fire-line"></i>
                  {event.heat}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {event.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <Link
              to="/category"
              className="mt-4 flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-arrow-left-line"></i>
              返回赛事分类
            </Link>
          </aside>
        </div>

        <section className="mt-16">
          <div className="mb-6 flex items-center gap-3">
            <span className="h-6 w-1 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              相关推荐赛事
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <EventCard key={item.id} event={item} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}